<template>
  <div class="app-layout" :class="{ 'sidebar-open': sidebarOpen }">
    <!-- 移动端遮罩 -->
    <div
      v-if="sidebarOpen"
      class="sidebar-overlay"
      @click="sidebarOpen = false"
    ></div>

    <Sidebar
      ref="sidebar"
      :active-tab="activeTab"
      :assistants="assistants"
      :topics="filteredTopics"
      :selected-assistant-id="selectedAssistantId"
      :selected-topic-id="selectedTopicId"
      :assistant-store-open="showAssistantStore"
      :mobile-open="sidebarOpen"
      :is-logged-in="user.isLoggedIn"
      :user="user"
      @tab-change="activeTab = $event"
      @select-assistant="handleSelectAssistant"
      @select-topic="handleSelectTopic"
      @add-assistant="handleAddAssistant"
      @add-topic="handleAddTopic"
      @delete-assistant="handleDeleteAssistant"
      @delete-topic="handleDeleteTopic"
      @edit-assistant="handleEditAssistant"
      @copy-assistant="handleCopyAssistant"
      @clear-assistant-topics="handleClearAssistantTopics"
      @save-assistant-library="handleSaveAssistantLibrary"
      @change-assistant-icon="handleChangeAssistantIcon"
      @sort-assistants="handleSortAssistants"
      @edit-topic="handleEditTopic"
      @user-change="handleUserChange"
      @show-login="openLogin"
      @logout="handleLogout"
    />

    <div class="main-area">
      <!-- 移动端顶部栏 -->
      <header v-if="isMobile" class="mobile-header">
        <button class="menu-toggle" @click="sidebarOpen = !sidebarOpen">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
          </svg>
        </button>
        <span class="mobile-title">
          {{ showAssistantStore ? '技能商店' : (currentTopic ? currentTopic.title : 'AI 聊天室') }}
        </span>
        <span class="mobile-spacer"></span>
      </header>

      <AssistantStore
        v-if="showAssistantStore"
        :added-template-ids="addedTemplateIds"
        :is-logged-in="user.isLoggedIn"
        :user="user"
        @add-from-template="handleAddFromTemplate"
        @require-login="handleRequireLoginFromStore"
      />
      <ChatPanel
        v-else
        :assistant="currentAssistant"
        :topic="currentTopic"
        :messages="currentMessages"
        :loading="chatLoading"
        :is-logged-in="user.isLoggedIn"
        @send="handleSend"
        @message-retry="handleMessageRetry"
        @message-delete="handleMessageDelete"
      />
    </div>

    <!-- 登录/注册弹窗（在根层级，不受侧边栏 transform 限制） -->
    <div v-if="loginVisible" class="login-overlay" @click.self="closeLogin">
      <div class="login-dialog">
        <button class="login-close" @click="closeLogin">✕</button>

        <!-- ====== 登录模式 ====== -->
        <template v-if="authMode === 'login'">
          <div class="login-header">
            <div class="login-icon">🔐</div>
            <h3>欢迎登录</h3>
            <p>登录后即可同步助手与话题</p>
          </div>
          <div class="login-form">
            <div class="form-field">
              <label>邮箱账号</label>
              <div class="input-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <input
                  v-model="loginForm.email"
                  type="email"
                  placeholder="请输入邮箱"
                  @keydown.enter="handleLogin"
                />
              </div>
            </div>
            <div class="form-field">
              <label>密码</label>
              <div class="input-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
                <input
                  v-model="loginForm.password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="请输入密码"
                  @keydown.enter="handleLogin"
                />
                <button class="toggle-pwd" @click="showPassword = !showPassword">
                  {{ showPassword ? '🙈' : '👁' }}
                </button>
              </div>
            </div>
            <div v-if="loginError" class="login-error">{{ loginError }}</div>
            <button class="login-submit" :disabled="loginLoading" @click="handleLogin">
              {{ loginLoading ? '登录中...' : '登 录' }}
            </button>
            <div class="login-footer">
              <span class="link-text" @click="switchMode('register')">没有账号？去注册</span>
              <span class="link-text" @click="switchMode('forgot')">忘记密码？</span>
            </div>
          </div>
        </template>

        <!-- ====== 注册模式 ====== -->
        <template v-else-if="authMode === 'register'">
          <div class="login-header">
            <div class="login-icon">📝</div>
            <h3>注册账号</h3>
            <p>通过邮箱验证码完成注册</p>
          </div>
          <div class="login-form">
            <div class="form-field">
              <label>邮箱</label>
              <div class="input-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <input
                  v-model="registerForm.email"
                  type="email"
                  placeholder="请输入邮箱"
                />
              </div>
            </div>
            <div class="form-field">
              <label>昵称 <span class="required">*</span></label>
              <div class="input-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                <input
                  v-model="registerForm.nickname"
                  type="text"
                  maxlength="20"
                  placeholder="请输入昵称（2-20位）"
                />
              </div>
            </div>
            <div class="form-field">
              <label>验证码</label>
              <div class="input-wrap code-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                </svg>
                <input
                  v-model="registerForm.code"
                  type="text"
                  maxlength="6"
                  placeholder="请输入6位验证码"
                />
                <button
                  class="send-code-btn"
                  :disabled="codeCountdown > 0 || sendingCode"
                  @click="sendRegisterCode"
                >
                  {{ sendingCode ? '发送中...' : codeCountdown > 0 ? `${codeCountdown}s 后重发` : '发送验证码' }}
                </button>
              </div>
            </div>
            <div class="form-field">
              <label>设置密码</label>
              <div class="input-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
                <input
                  v-model="registerForm.password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="设置登录密码（至少6位）"
                />
                <button class="toggle-pwd" @click="showPassword = !showPassword">
                  {{ showPassword ? '🙈' : '👁' }}
                </button>
              </div>
            </div>
            <div class="form-field">
              <label>确认密码</label>
              <div class="input-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
                <input
                  v-model="registerForm.confirmPassword"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="请再次输入密码"
                  @keydown.enter="handleRegister"
                />
              </div>
            </div>
            <div v-if="loginError" class="login-error">{{ loginError }}</div>
            <button class="login-submit" :disabled="loginLoading" @click="handleRegister">
              {{ loginLoading ? '注册中...' : '注 册' }}
            </button>
            <div class="login-footer">
              <span class="link-text" @click="switchMode('login')">已有账号？去登录</span>
            </div>
          </div>
        </template>

        <!-- ====== 忘记密码模式 ====== -->
        <template v-else-if="authMode === 'forgot'">
          <div class="login-header">
            <div class="login-icon">🔑</div>
            <h3>重置密码</h3>
            <p>通过邮箱验证码重置密码</p>
          </div>
          <div class="login-form">
            <div class="form-field">
              <label>邮箱</label>
              <div class="input-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <input
                  v-model="forgotForm.email"
                  type="email"
                  placeholder="请输入注册邮箱"
                />
              </div>
            </div>
            <div class="form-field">
              <label>验证码</label>
              <div class="input-wrap code-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                </svg>
                <input
                  v-model="forgotForm.code"
                  type="text"
                  maxlength="6"
                  placeholder="请输入6位验证码"
                />
                <button
                  class="send-code-btn"
                  :disabled="codeCountdown > 0 || sendingCode"
                  @click="sendForgotCode"
                >
                  {{ sendingCode ? '发送中...' : codeCountdown > 0 ? `${codeCountdown}s 后重发` : '发送验证码' }}
                </button>
              </div>
            </div>
            <div class="form-field">
              <label>新密码</label>
              <div class="input-wrap">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#bbb">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
                <input
                  v-model="forgotForm.password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="设置新密码（至少6位）"
                  @keydown.enter="handleResetPassword"
                />
                <button class="toggle-pwd" @click="showPassword = !showPassword">
                  {{ showPassword ? '🙈' : '👁' }}
                </button>
              </div>
            </div>
            <div v-if="loginError" class="login-error">{{ loginError }}</div>
            <button class="login-submit" :disabled="loginLoading" @click="handleResetPassword">
              {{ loginLoading ? '重置中...' : '重置密码' }}
            </button>
            <div class="login-footer">
              <span class="link-text" @click="switchMode('login')">返回登录</span>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from './components/Sidebar.vue'
import ChatPanel from './components/ChatPanel.vue'
import AssistantStore from './components/AssistantStore.vue'
import {
  defaultAssistants,
  defaultTopics,
  defaultMessages,
  generateId
} from './utils/helpers'
import { streamChatDefault, streamChatLoggedIn, getOrCreateUserId, getUserAssistantsByUserId, saveDefaultTopicByUserAssistantId, getTopicsByUserAssistantId, deleteTopicByUserAssistantId, editUserAssistant, deleteUserAssistant, getChatMemoriesBySessionId, sendEmailCode, registerUser, loginUser, resetPassword } from './api'
import { saveGuestHistory, getGuestHistory, clearGuestHistory } from './api/chatHistory'

export default {
  name: 'App',
  components: { Sidebar, ChatPanel, AssistantStore },
  data() {
    return {
      activeTab: 'assistant',
      assistants: [...defaultAssistants],
      topics: [...defaultTopics],
      messages: { ...defaultMessages },
      selectedAssistantId: 1,
      selectedTopicId: 1,
      chatLoading: false,
      showAssistantStore: false,
      addedTemplateIds: [],
      sidebarOpen: false,
      isMobile: window.innerWidth <= 768,
      user: {
        isLoggedIn: false,
        nickname: '未登录',
        email: '',
        avatar: ''
      },
      // 登录弹窗相关数据
      loginVisible: false,
      loginLoading: false,
      loginError: '',
      showPassword: false,
      authMode: 'login',
      sendingCode: false,
      codeCountdown: 0,
      countdownTimer: null,
      loginForm: {
        email: '',
        password: ''
      },
      registerForm: {
        email: '',
        nickname: '',
        code: '',
        password: '',
        confirmPassword: ''
      },
      forgotForm: {
        email: '',
        code: '',
        password: ''
      }
    }
  },
  computed: {
    filteredTopics() {
      return this.topics
        .filter(t => t.assistantId === this.selectedAssistantId)
        .sort((a, b) => b.updatedAt - a.updatedAt)
    },
    currentAssistant() {
      return this.assistants.find(a => a.id === this.selectedAssistantId) || null
    },
    currentTopic() {
      return this.topics.find(t => t.id === this.selectedTopicId) || null
    },
    currentMessages() {
      if (!this.selectedTopicId) return []
      return this.messages[this.selectedTopicId] || []
    }
  },
  mounted() {
    var self = this
    window.addEventListener('resize', this.handleResize)
    const saved = localStorage.getItem('ai-chat-user')
    if (saved) {
      try {
        this.user = JSON.parse(saved)
        // 同步到 Sidebar
        if (this.$refs.sidebar) {
          this.$refs.sidebar.user = JSON.parse(saved)
        }
      } catch (e) {
        localStorage.removeItem('ai-chat-user')
      }
    }

    // 先还原默认助手的本地聊天记录（无论登录与否，默认助手都走本地缓存）
    self.restoreGuestHistory()
    // 已登录且有 userId：拉取后端助手列表合并到本地列表（保留默认助手数据）
    if (self.user && self.user.isLoggedIn && self.user.userId) {
      self.loadUserAssistantsFromBackend(self.user.userId)
    } else {
      // 未登录用户：确保 userId 已生成
      getOrCreateUserId()
    }
  },
  watch: {
    // 默认助手的本地聊天记录始终保存到 localStorage（无论登录与否）
    messages: {
      handler() {
        this.persistGuestHistory()
      },
      deep: true
    },
    topics: {
      handler() {
        this.persistGuestHistory()
      },
      deep: true
    },
    assistants: {
      handler() {
        this.persistGuestHistory()
      },
      deep: true
    },
    'user.isLoggedIn'(val) {
      var self = this
      if (val) {
        // 登录时不清空游客历史，保留默认助手的本地聊天记录
        // 仅从后端拉取已添加的助手列表，合并到当前列表（保留默认助手）
        if (self.user && self.user.userId) {
          self.loadUserAssistantsFromBackend(self.user.userId)
        }
      } else {
        // 退出登录后，移除后端助手，保留默认助手的本地话题和聊天记录
        self.resetToDefaultAssistants()
        // 重新生成 userId 并保存历史
        getOrCreateUserId()
        self.persistGuestHistory()
      }
    },
    // ===== 选中已添加助手 + 切到话题 tab → 仅从后台加载话题列表，不保留默认话题 =====
    activeTab: function activeTabWatcher(newTab) {
      if (newTab !== 'topic') return
      var self = this
      if (!self.user || !self.user.isLoggedIn || !self.user.userId) return
      var astId = self.selectedAssistantId
      if (!astId) return
      var ast = self.assistants.find(function (a) { return a.id === astId }) || null
      if (!ast || !ast.userAssistantId) return
      // 登录态选中已添加助手 + 切到话题 tab → 只从后台接口获取话题列表
      self.loadTopicsFromBackend(ast)
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // ========== 加载某个助手下的后端话题列表（仅后台返回的话题，不保留默认话题）==========
    loadTopicsFromBackend(ast) {
      var self = this
      var userAssistantId = ast.userAssistantId
      // 缓存：同一个 userAssistantId 已加载过则跳过
      if (!self._loadedTopicsCache) self._loadedTopicsCache = {}
      if (self._loadedTopicsCache[userAssistantId]) return
      self._loadedTopicsCache[userAssistantId] = true

      getTopicsByUserAssistantId(userAssistantId).then(function (json) {
        if (!json || json.code !== '200' && json.code !== 200 && !json.success) {
          console.warn('加载话题列表失败:', (json && json.message) || json)
          return
        }
        var list = Array.isArray(json.data) ? json.data : []
        // 移除该助手下旧的本地话题（保留其他助手的）
        var oldTopics = self.topics.filter(function (t) { return t.assistantId !== ast.id })
        // 只用后端返回的话题替换，不创建/保留默认话题
        list.forEach(function (item) {
          var localTopicId = 'topic-be-' + item.id
          oldTopics.unshift({
            id: localTopicId,
            assistantId: ast.id,
            title: item.title || '新对话',
            updatedAt: Date.now(),
            sessionId: item.id
          })
          if (!self.messages[localTopicId]) {
            self.$set(self.messages, localTopicId, [])
          }
        })
        self.topics = oldTopics
        // 选中第一个该助手下的话题（后端无话题则为空）
        var firstTopic = self.topics.find(function (t) { return t.assistantId === ast.id })
        self.selectedTopicId = firstTopic ? firstTopic.id : null
        // 自动加载第一个话题的聊天记录
        if (firstTopic) {
          self.loadChatMemoriesForTopic(firstTopic.id)
        }
      }).catch(function (err) {
        console.error('加载话题列表失败:', err)
      })
    },
    // ========== 加载某个话题的后端聊天记录 ==========
    loadChatMemoriesForTopic(topicId) {
      var self = this
      if (!self.user || !self.user.isLoggedIn) return
      var topic = self.topics.find(function (t) { return t.id === topicId }) || null
      if (!topic || !topic.sessionId) return
      // 已加载过则跳过
      if (self._loadedMemoriesCache && self._loadedMemoriesCache[topicId]) return
      if (!self._loadedMemoriesCache) self._loadedMemoriesCache = {}
      self._loadedMemoriesCache[topicId] = true
      getChatMemoriesBySessionId(topic.sessionId).then(function (json) {
        if (!json || json.code !== '200' && json.code !== 200 && !json.success) {
          console.warn('加载聊天记录失败:', (json && json.message) || json)
          return
        }
        var list = Array.isArray(json.data) ? json.data : []
        // 仅当本地无消息时才填充，避免覆盖正在进行的会话
        if (self.messages[topicId] && self.messages[topicId].length > 0) return
        var msgs = []
        list.forEach(function (item) {
          var role = item.type === 'USER' ? 'user' : 'assistant'
          msgs.push({
            id: 'mem-' + item.id,
            role: role,
            type: 'text',
            content: item.content || '',
            timestamp: item.timestamp ? new Date(item.timestamp).getTime() : Date.now()
          })
        })
        self.$set(self.messages, topicId, msgs)
      }).catch(function (err) {
        console.error('加载聊天记录失败:', err)
      })
    },
    // ========== 已登录用户拉取后端助手列表（保留默认助手的本地话题和聊天记录）==========
    loadUserAssistantsFromBackend(userId) {
      var self = this
      getUserAssistantsByUserId(userId).then(function (json) {
        if (!json || json.code !== '200' && json.code !== 200 && !json.success) {
          console.warn('加载用户助手列表失败:', (json && json.message) || json)
          return
        }
        var list = Array.isArray(json.data) ? json.data : []
        // 保留当前已有的默认助手（id=1）及其本地话题和消息
        var baseAssistants = self.assistants.filter(function (a) { return a.id === 1 || !a.userAssistantId })
        // 移除旧的后端助手相关的话题
        self.topics = self.topics.filter(function (t) {
          var ast = baseAssistants.find(function (a) { return a.id === t.assistantId })
          return !!ast
        })
        list.forEach(function (item) {
          if (!item || !item.id) return
          var localId = 'ua-' + item.id
          // 避免重复添加
          if (baseAssistants.find(function (a) { return a.userAssistantId === item.id })) return
          baseAssistants.push({
            id: localId,
            name: item.name || '助手',
            icon: item.emoji || '🤖',
            description: '',
            userAssistantId: item.id,
            prompt: item.prompt || '',
            llmCode: item.llmCode || ''
          })
        })
        self.assistants = baseAssistants
        // 选中默认助手 & 默认话题（保留当前的本地选择）
        if (!self.selectedAssistantId || !self.assistants.find(function (a) { return a.id === self.selectedAssistantId })) {
          self.selectedAssistantId = baseAssistants[0] ? baseAssistants[0].id : 1
        }
        if (!self.selectedTopicId || !self.topics.find(function (t) { return t.id === self.selectedTopicId })) {
          var defaultTopic = self.topics.find(function (t) { return t.assistantId === self.selectedAssistantId })
          self.selectedTopicId = defaultTopic ? defaultTopic.id : 1
        }
      }).catch(function (err) {
        console.error('加载用户助手列表失败:', err)
      })
    },
    // 退出登录后移除后端助手，保留默认助手的本地话题和聊天记录（从游客历史还原）
    resetToDefaultAssistants() {
      var self = this
      // 先尝试从游客历史还原默认助手的聊天记录
      const history = getGuestHistory()
      if (history && history.messages && history.topics && history.assistants) {
        // 只保留默认助手（id=1 或无 userAssistantId）的数据
        self.assistants = history.assistants.filter(function (a) { return a.id === 1 || !a.userAssistantId })
        if (self.assistants.length === 0) {
          self.assistants = [...defaultAssistants]
        }
        var defaultAstIds = self.assistants.map(function (a) { return a.id })
        self.topics = history.topics.filter(function (t) { return defaultAstIds.indexOf(t.assistantId) !== -1 })
        if (self.topics.length === 0) {
          self.topics = [...defaultTopics]
        }
        // 只保留默认助手相关话题的消息
        var validTopicIds = self.topics.map(function (t) { return t.id })
        var newMessages = {}
        validTopicIds.forEach(function (tid) {
          if (history.messages[tid]) {
            newMessages[tid] = history.messages[tid]
          }
        })
        self.messages = newMessages
      } else {
        self.assistants = [...defaultAssistants]
        self.topics = [...defaultTopics]
        self.messages = { ...defaultMessages }
      }
      self.addedTemplateIds = []
      self.selectedAssistantId = self.assistants[0] ? self.assistants[0].id : 1
      var firstTopic = self.topics.find(function (t) { return t.assistantId === self.selectedAssistantId })
      self.selectedTopicId = firstTopic ? firstTopic.id : 1
    },
    // ========== 游客历史持久化（默认助手的本地聊天记录，登录与否都保存）==========
    persistGuestHistory() {
      // 只持久化默认助手（无 userAssistantId）的数据
      var defaultAstIds = this.assistants
        .filter(function (a) { return !a.userAssistantId })
        .map(function (a) { return a.id })
      if (defaultAstIds.length === 0) return
      var topicsToSave = this.topics.filter(function (t) { return defaultAstIds.indexOf(t.assistantId) !== -1 })
      var topicIds = topicsToSave.map(function (t) { return t.id })
      var messagesToSave = {}
      topicIds.forEach(function (tid) {
        if (this.messages[tid]) {
          messagesToSave[tid] = this.messages[tid]
        }
      }.bind(this))
      saveGuestHistory({
        assistants: this.assistants.filter(function (a) { return !a.userAssistantId }),
        topics: topicsToSave,
        messages: messagesToSave,
        selectedAssistantId: this.selectedAssistantId,
        selectedTopicId: this.selectedTopicId
      })
    },
    restoreGuestHistory() {
      const history = getGuestHistory()
      if (!history) return
      if (history.assistants && history.assistants.length > 0) {
        this.assistants = history.assistants
      }
      if (history.topics) {
        this.topics = history.topics
      }
      if (history.messages) {
        this.messages = history.messages
      }
      if (history.selectedAssistantId) {
        this.selectedAssistantId = history.selectedAssistantId
      }
      if (history.selectedTopicId) {
        this.selectedTopicId = history.selectedTopicId
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 768
      if (!this.isMobile) this.sidebarOpen = false
    },
    handleSelectAssistant(id) {
      this.selectedAssistantId = id
      this.showAssistantStore = false
      const topics = this.filteredTopics
      if (topics.length > 0) {
        this.selectedTopicId = topics[0].id
      } else {
        this.selectedTopicId = null
      }
      if (this.isMobile) this.sidebarOpen = false
      // 登录状态 + 后端助手 → 自动切换到话题 tab 并触发话题列表加载
      var self = this
      if (self.user && self.user.isLoggedIn && self.user.userId) {
        var ast = self.assistants.find(function (a) { return a.id === id }) || null
        if (ast && ast.userAssistantId) {
          self.activeTab = 'topic'
          self.loadTopicsFromBackend(ast)
        }
      }
    },
    handleSelectTopic(id) {
      this.selectedTopicId = id
      this.showAssistantStore = false
      if (this.isMobile) this.sidebarOpen = false
      // 登录状态 + 话题有 sessionId → 加载后端聊天记录
      this.loadChatMemoriesForTopic(id)
    },
    handleAddAssistant() {
      this.showAssistantStore = !this.showAssistantStore
      if (this.isMobile) this.sidebarOpen = false
    },
    handleUserChange(userInfo) {
      this.user = userInfo
    },
    handleRequireLogin() {
      this.openLogin()
    },
    handleRequireLoginFromStore() {
      this.openLogin()
    },
    // ========== 登录弹窗控制 ==========
    openLogin() {
      this.authMode = 'login'
      this.loginVisible = true
      this.loginError = ''
    },
    closeLogin() {
      this.loginVisible = false
      this.loginError = ''
    },
    switchMode(mode) {
      this.authMode = mode
      this.loginError = ''
    },
    validateEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },
    startCountdown() {
      this.codeCountdown = 60
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      var self = this
      this.countdownTimer = setInterval(function () {
        self.codeCountdown--
        if (self.codeCountdown <= 0) {
          clearInterval(self.countdownTimer)
          self.countdownTimer = null
        }
      }, 1000)
    },
    sendRegisterCode() {
      var self = this
      self.loginError = ''
      var email = self.registerForm.email.trim()
      if (!email) {
        self.loginError = '请输入邮箱'
        return
      }
      if (!self.validateEmail(email)) {
        self.loginError = '邮箱格式不正确'
        return
      }
      self.sendingCode = true
      sendEmailCode(email).then(function (json) {
        self.sendingCode = false
        if (json && (json.code === '200' || json.code === 200 || json.success)) {
          self.startCountdown()
          alert('验证码已发送至 ' + email + '，请查收邮件')
        } else {
          self.loginError = (json && json.message) || '发送验证码失败'
        }
      }).catch(function (err) {
        self.sendingCode = false
        self.loginError = '网络请求失败，请检查后端服务是否启动'
        console.error('发送验证码失败:', err)
      })
    },
    sendForgotCode() {
      var self = this
      self.loginError = ''
      var email = self.forgotForm.email.trim()
      if (!email) {
        self.loginError = '请输入邮箱'
        return
      }
      if (!self.validateEmail(email)) {
        self.loginError = '邮箱格式不正确'
        return
      }
      self.sendingCode = true
      sendEmailCode(email).then(function (json) {
        self.sendingCode = false
        if (json && (json.code === '200' || json.code === 200 || json.success)) {
          self.startCountdown()
          alert('验证码已发送至 ' + email + '，请查收邮件')
        } else {
          self.loginError = (json && json.message) || '发送验证码失败'
        }
      }).catch(function (err) {
        self.sendingCode = false
        self.loginError = '网络请求失败，请检查后端服务是否启动'
        console.error('发送验证码失败:', err)
      })
    },
    handleLogin() {
      var self = this
      self.loginError = ''
      var email = self.loginForm.email.trim()
      var password = self.loginForm.password
      if (!email) {
        self.loginError = '请输入邮箱'
        return
      }
      if (!self.validateEmail(email)) {
        self.loginError = '邮箱格式不正确'
        return
      }
      if (!password) {
        self.loginError = '请输入密码'
        return
      }
      self.loginLoading = true
      loginUser(email, password).then(function (json) {
        self.loginLoading = false
        if (json && (json.code === '200' || json.code === 200 || json.success)) {
          var respData = json.data || {}
          self.user = {
            isLoggedIn: true,
            userId: respData.id,
            nickname: respData.nickName || email.split('@')[0],
            email: email,
            avatar: respData.avatar || ''
          }
          localStorage.setItem('ai-chat-user', JSON.stringify(self.user))
          self.loginVisible = false
          self.loginForm = { email: '', password: '' }
        } else {
          self.loginError = (json && json.message) || '登录失败，请检查邮箱或密码'
        }
      }).catch(function (err) {
        self.loginLoading = false
        self.loginError = '网络请求失败，请检查后端服务是否启动'
        console.error('登录失败:', err)
      })
    },
    handleRegister() {
      var self = this
      self.loginError = ''
      var f = self.registerForm
      if (!f.email.trim()) {
        self.loginError = '请输入邮箱'
        return
      }
      if (!self.validateEmail(f.email.trim())) {
        self.loginError = '邮箱格式不正确'
        return
      }
      if (!f.nickname || !f.nickname.trim()) {
        self.loginError = '请输入昵称'
        return
      }
      var nickname = f.nickname.trim()
      if (nickname.length < 2 || nickname.length > 20) {
        self.loginError = '昵称长度需要2-20位'
        return
      }
      if (!f.code.trim()) {
        self.loginError = '请输入验证码'
        return
      }
      if (!f.password || f.password.length < 6) {
        self.loginError = '密码至少6位'
        return
      }
      if (f.password !== f.confirmPassword) {
        self.loginError = '两次密码不一致'
        return
      }
      self.loginLoading = true
      registerUser({
        email: f.email.trim(),
        password: f.password,
        nickName: nickname,
        code: f.code.trim()
      }).then(function (json) {
        self.loginLoading = false
        if (json && (json.code === '200' || json.code === 200 || json.success)) {
          var respData = json.data || {}
          self.user = {
            isLoggedIn: true,
            userId: respData.id,
            nickname: respData.nickName || nickname,
            email: f.email.trim(),
            avatar: respData.avatar || ''
          }
          localStorage.setItem('ai-chat-user', JSON.stringify(self.user))
          self.loginVisible = false
          self.registerForm = { email: '', nickname: '', code: '', password: '', confirmPassword: '' }
        } else {
          self.loginError = (json && json.message) || '注册失败，请稍后重试'
        }
      }).catch(function (err) {
        self.loginLoading = false
        self.loginError = '网络请求失败，请检查后端服务是否启动'
        console.error('注册失败:', err)
      })
    },
    handleResetPassword() {
      var self = this
      self.loginError = ''
      var f = self.forgotForm
      if (!f.email.trim()) {
        self.loginError = '请输入邮箱'
        return
      }
      if (!self.validateEmail(f.email.trim())) {
        self.loginError = '邮箱格式不正确'
        return
      }
      if (!f.code.trim()) {
        self.loginError = '请输入验证码'
        return
      }
      if (!f.password || f.password.length < 6) {
        self.loginError = '密码至少6位'
        return
      }
      self.loginLoading = true
      resetPassword({
        email: f.email.trim(),
        code: f.code.trim(),
        password: f.password
      }).then(function (json) {
        self.loginLoading = false
        if (json && (json.code === '200' || json.code === 200 || json.success)) {
          self.loginVisible = false
          self.forgotForm = { email: '', code: '', password: '' }
          self.authMode = 'login'
          self.loginForm.email = f.email.trim()
          alert('密码重置成功，请使用新密码登录')
        } else {
          self.loginError = (json && json.message) || '重置密码失败，请稍后重试'
        }
      }).catch(function (err) {
        self.loginLoading = false
        self.loginError = '网络请求失败，请检查后端服务是否启动'
        console.error('重置密码失败:', err)
      })
    },
    handleLogout() {
      this.user = {
        isLoggedIn: false,
        nickname: '未登录',
        email: '',
        avatar: ''
      }
      localStorage.removeItem('ai-chat-user')
    },
    handleAddFromTemplate(tpl, meta) {
      const assistant = {
        id: generateId(),
        name: tpl.name,
        icon: tpl.icon,
        description: tpl.description,
        templateId: tpl.id,
        skills: tpl.skills
      }
      if (meta && meta.userAssistantId) {
        assistant.userAssistantId = meta.userAssistantId
      }
      this.assistants.push(assistant)
      this.addedTemplateIds.push(tpl.id)
      this.selectedAssistantId = assistant.id
      this.activeTab = 'assistant'
      this.showAssistantStore = false
      const topic = {
        id: generateId(),
        assistantId: assistant.id,
        title: '新对话',
        updatedAt: Date.now()
      }
      this.topics.unshift(topic)
      this.messages[topic.id] = []
      this.selectedTopicId = topic.id
    },
    handleAddTopic() {
      var self = this
      if (!this.selectedAssistantId) {
        alert('请先选择一个助手')
        return
      }
      const title = prompt('请输入话题名称：')
      if (!title || !title.trim()) return
      const topic = {
        id: generateId(),
        assistantId: this.selectedAssistantId,
        title: title.trim(),
        updatedAt: Date.now(),
        sessionId: null
      }
      this.topics.unshift(topic)
      this.messages[topic.id] = []
      this.selectedTopicId = topic.id
      this.activeTab = 'topic'

      // 已登录 + 选中助手有 userAssistantId → 调后端保存话题
      if (self.user && self.user.isLoggedIn && self.user.userId) {
        var ast = self.assistants.find(function (a) { return a.id === self.selectedAssistantId }) || null
        if (ast && ast.userAssistantId) {
          saveDefaultTopicByUserAssistantId(ast.userAssistantId, {
            title: topic.title,
            userId: self.user.userId
          }).then(function (json) {
            // 保存成功后把后端返回的 sessionId 存到本地 topic 上（使用 $set 确保响应式）
            if (json && (json.code === '200' || json.code === 200 || json.success) && json.data) {
              var sid = (typeof json.data === 'object') ? json.data.id : json.data
              if (sid) {
                self.$set(topic, 'sessionId', sid)
              }
            }
          }).catch(function (err) {
            if (err && err.status !== 400) {
              console.error('保存话题失败:', err)
            }
          })
        }
      }
    },
    handleDeleteAssistant(id) {
      var self = this
      if (this.assistants.length <= 1) {
        alert('至少保留一个助手')
        return
      }
      if (!confirm('确定删除该助手及其所有话题？')) return
      var ast = this.assistants.find(function (a) { return a.id === id }) || null
      // 未登录 或 默认助手（无 userAssistantId）→ 仅本地删除
      if (!self.user || !self.user.isLoggedIn || !self.user.userId || !ast || !ast.userAssistantId) {
        self._deleteAssistantLocally(id)
        return
      }
      // 登录状态 + 后端助手 → 先调后端 DELETE 接口
      deleteUserAssistant(self.user.userId, ast.userAssistantId).then(function (json) {
        if (json && (json.code === '200' || json.code === 200 || json.success)) {
          self._deleteAssistantLocally(id)
        } else {
          alert((json && json.message) || '删除助手失败')
        }
      }).catch(function (err) {
        if (err && err.status !== 400) {
          alert((err && err.message) || '网络错误，删除失败')
        }
        console.error('删除助手失败:', err)
      })
    },
    // 本地删除助手（同步删除其下话题和消息）
    _deleteAssistantLocally(id) {
      var self = this
      var topicIds = self.topics.filter(function (t) { return t.assistantId === id }).map(function (t) { return t.id })
      self.topics = self.topics.filter(function (t) { return t.assistantId !== id })
      topicIds.forEach(function (tid) {
        self.$delete(self.messages, tid)
      })
      self.assistants = self.assistants.filter(function (a) { return a.id !== id })
      if (self.selectedAssistantId === id) {
        self.selectedAssistantId = self.assistants[0] ? self.assistants[0].id : null
        var topics = self.filteredTopics
        self.selectedTopicId = topics.length > 0 ? topics[0].id : null
      }
    },
    handleDeleteTopic(id) {
      if (!confirm('确定删除该话题？')) return
      var self = this
      var topic = this.topics.find(function (t) { return t.id === id })
      if (!topic) return
      // 查找话题所属的助手
      var ast = this.assistants.find(function (a) { return a.id === topic.assistantId })

      // 本地删除辅助函数
      function removeLocal() {
        self.topics = self.topics.filter(function (t) { return t.id !== id })
        self.$delete(self.messages, id)
        if (self.selectedTopicId === id) {
          var topics = self.filteredTopics
          self.selectedTopicId = topics.length > 0 ? topics[0].id : null
        }
      }

      // 未登录或助手没有 userAssistantId → 仅本地删除
      if (!self.user.isLoggedIn || !ast || !ast.userAssistantId) {
        removeLocal()
        return
      }

      // 登录状态且有 userAssistantId → 调用后端接口删除
      deleteTopicByUserAssistantId(ast.userAssistantId).then(function (json) {
        if (json && (json.code === '200' || json.code === 200 || json.success)) {
          removeLocal()
        } else {
          alert((json && json.message) || '删除话题失败')
        }
      }).catch(function (err) {
        // 400 错误已由全局拦截器弹窗提示，此处避免重复弹窗
        if (err && err.status !== 400) {
          alert('网络错误，删除失败')
        }
        console.error('删除话题失败:', err)
      })
    },
    // ========== 编辑助手 ==========
    handleEditAssistant(id) {
      var self = this
      const assistant = this.assistants.find(a => a.id === id)
      if (!assistant) return
      const newName = prompt('编辑助手名称：', assistant.name)
      if (!newName || !newName.trim()) return
      const trimmedName = newName.trim()

      // 未登录或助手无 userAssistantId → 仅本地更新
      if (!self.user.isLoggedIn || !assistant.userAssistantId) {
        assistant.name = trimmedName
        return
      }

      // 登录状态且有 userAssistantId → 调用后端接口编辑助手
      editUserAssistant(self.user.userId, assistant.userAssistantId, trimmedName).then(function (json) {
        if (json && (json.code === '200' || json.code === 200 || json.success)) {
          assistant.name = trimmedName
        } else {
          alert((json && json.message) || '编辑助手失败')
        }
      }).catch(function (err) {
        // 400 错误已由全局拦截器弹窗提示，此处避免重复弹窗
        if (err && err.status !== 400) {
          alert('网络错误，编辑失败')
        }
        console.error('编辑助手失败:', err)
      })
    },
    // ========== 复制助手 ==========
    handleCopyAssistant(id) {
      const original = this.assistants.find(a => a.id === id)
      if (!original) return
      const copy = {
        id: generateId(),
        name: original.name + ' (副本)',
        icon: original.icon,
        description: original.description || '复制的助手',
        templateId: original.templateId
      }
      this.assistants.push(copy)
      // 复制该助手下的话题
      const originalTopics = this.topics.filter(t => t.assistantId === id)
      originalTopics.forEach(t => {
        const newTopic = {
          id: generateId(),
          assistantId: copy.id,
          title: t.title,
          updatedAt: Date.now()
        }
        this.topics.push(newTopic)
        // 复制消息
        if (this.messages[t.id]) {
          this.$set(this.messages, newTopic.id, JSON.parse(JSON.stringify(this.messages[t.id])))
        }
      })
      this.selectedAssistantId = copy.id
      this.activeTab = 'assistant'
    },
    // ========== 清空助手下的话题 ==========
    handleClearAssistantTopics(id) {
      const assistant = this.assistants.find(a => a.id === id)
      if (!assistant) return
      if (!confirm(`确定清空助手「${assistant.name}」的所有话题吗？此操作不可恢复。`)) return
      const topicIds = this.topics.filter(t => t.assistantId === id).map(t => t.id)
      this.topics = this.topics.filter(t => t.assistantId !== id)
      topicIds.forEach(tid => this.$delete(this.messages, tid))
      if (this.selectedAssistantId === id) {
        this.selectedTopicId = null
      }
    },
    // ========== 保存到助手库 ==========
    handleSaveAssistantLibrary(id) {
      alert('已保存到助手库（演示功能，可扩展至真实服务端）')
    },
    // ========== 修改助手图标 ==========
    handleChangeAssistantIcon(id) {
      const assistant = this.assistants.find(a => a.id === id)
      if (!assistant) return
      const icons = ['🤖', '🧠', '💡', '⚡', '🎯', '🔧', '📚', '🎨', '🚀', '💬', '✍️', '🔬', '📊', '🎮', '🌐']
      const pick = prompt(`选择新图标（粘贴任意 emoji 或使用下列之一）：\n${icons.join(' ')}`, assistant.icon)
      if (!pick || !pick.trim()) return
      assistant.icon = pick.trim()
    },
    // ========== 助手排序 ==========
    handleSortAssistants(order) {
      this.assistants.sort((a, b) => {
        const cmp = a.name.localeCompare(b.name, 'zh-Hans-CN')
        return order === 'asc' ? cmp : -cmp
      })
    },
    // ========== 编辑话题 ==========
    handleEditTopic(id) {
      const topic = this.topics.find(t => t.id === id)
      if (!topic) return
      const newTitle = prompt('编辑话题名称：', topic.title)
      if (!newTitle || !newTitle.trim()) return
      topic.title = newTitle.trim()
    },
    handleSend(payload) {
      if (!this.selectedTopicId) {
        this.handleAddTopic()
        if (!this.selectedTopicId) return
      }

      const { content, type, model, webSearch } = payload
      const topicId = this.selectedTopicId
      var self = this

      if (!this.messages[topicId]) {
        this.$set(this.messages, topicId, [])
      }

      const userMsg = {
        id: generateId(),
        role: 'user',
        type,
        content,
        model,
        webSearch: !!webSearch,
        timestamp: Date.now()
      }
      this.messages[topicId].push(userMsg)

      const topic = this.topics.find(t => t.id === topicId)
      if (topic) {
        if (topic.title === '新对话') {
          topic.title = content.length > 20 ? content.slice(0, 20) + '...' : content
        }
        topic.updatedAt = Date.now()
      }

      const assistantMsg = {
        id: generateId(),
        role: 'assistant',
        type: 'text',
        content: '',
        timestamp: Date.now()
      }
      this.messages[topicId].push(assistantMsg)

      this.chatLoading = true
      // 默认助手（id === 1 或无 userAssistantId）永远走默认聊天接口 /api/chat/default
      // 已登录 + 当前助手为后端助手（有 userAssistantId）才走 /api/chat
      var currentAst = self.assistants.find(function (a) { return a.id === self.selectedAssistantId }) || null
      var isDefaultAssistant = !currentAst || !currentAst.userAssistantId || currentAst.id === 1
      var chatPromise
      if (self.user && self.user.isLoggedIn && self.user.userId && !isDefaultAssistant) {
        // 取当前话题的 sessionId（后端话题 ID），用于聊天接口的 id 字段
        var currentTopic = self.topics.find(function (t) { return t.id === self.selectedTopicId }) || null
        var chatSessionId = (currentTopic && currentTopic.sessionId) ? currentTopic.sessionId : ''
        chatPromise = streamChatLoggedIn(
          currentAst.userAssistantId,
          content,
          self.user.userId,
          chatSessionId,
          function(chunk, fullText) { assistantMsg.content = fullText },
          !!webSearch
        )
      } else {
        chatPromise = streamChatDefault(content, function(chunk, fullText) {
          assistantMsg.content = fullText
        }, !!webSearch)
      }
      chatPromise.then(function() {
        if (!assistantMsg.content) {
          assistantMsg.content = '（未收到回复内容）'
        }
        self.chatLoading = false
      }).catch(function(e) {
        assistantMsg.content = '⚠️ 请求失败：' + e.message + '\n\n（请确认后端服务已启动）'
        self.chatLoading = false
      })
    },
    handleMessageDelete(message) {
      const topicId = this.selectedTopicId
      if (!topicId || !this.messages[topicId]) return

      const idx = this.messages[topicId].findIndex(function(m) { return m.id === message.id })
      if (idx !== -1) {
        this.messages[topicId].splice(idx, 1)
      }
    },
    handleMessageRetry(message) {
      const topicId = this.selectedTopicId
      if (!topicId || !this.messages[topicId]) return

      const msgs = this.messages[topicId]
      const idx = msgs.findIndex(function(m) { return m.id === message.id })
      if (idx === -1) return

      let userContent = ''
      let originalWebSearch = false
      for (let i = idx - 1; i >= 0; i--) {
        if (msgs[i].role === 'user') {
          userContent = msgs[i].content
          originalWebSearch = !!msgs[i].webSearch
          break
        }
      }

      msgs.splice(idx, 1)

      if (userContent) {
        const userMsg = {
          id: generateId(),
          role: 'user',
          type: 'text',
          content: userContent,
          webSearch: originalWebSearch,
          timestamp: Date.now()
        }
        msgs.push(userMsg)

        const assistantMsg = {
          id: generateId(),
          role: 'assistant',
          type: 'text',
          content: '',
          timestamp: Date.now()
        }
        msgs.push(assistantMsg)

        var self = this
        self.chatLoading = true
        // 默认助手永远走默认聊天接口；已登录 + 后端助手才走 /api/chat
        var retryAst = self.assistants.find(function (a) { return a.id === self.selectedAssistantId }) || null
        var isDefaultAssistant = !retryAst || !retryAst.userAssistantId || retryAst.id === 1
        var retryPromise
        if (self.user && self.user.isLoggedIn && self.user.userId && !isDefaultAssistant) {
          // 取当前话题的 sessionId（后端话题 ID），用于聊天接口的 id 字段
          var retryTopic = self.topics.find(function (t) { return t.id === self.selectedTopicId }) || null
          var retrySessionId = (retryTopic && retryTopic.sessionId) ? retryTopic.sessionId : ''
          retryPromise = streamChatLoggedIn(
            retryAst.userAssistantId,
            userContent,
            self.user.userId,
            retrySessionId,
            function(chunk, fullText) { assistantMsg.content = fullText },
            originalWebSearch
          )
        } else {
          retryPromise = streamChatDefault(userContent, function(chunk, fullText) {
            assistantMsg.content = fullText
          }, originalWebSearch)
        }
        retryPromise.then(function() {
          self.chatLoading = false
        }).catch(function(e) {
          assistantMsg.content = '⚠️ 请求失败：' + e.message
          self.chatLoading = false
        })
      }
    }
  }
}
</script>

<style scoped>
.app-layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100vh;
}

.sidebar-overlay {
  display: none;
}

.mobile-header {
  display: none;
}

/* ====== 移动端 ≤768px ====== */
@media (max-width: 768px) {
  .app-layout {
    position: relative;
  }

  .sidebar-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 999;
    animation: fadeIn 0.2s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .mobile-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: #fff;
    border-bottom: 1px solid #e8e8e8;
    flex-shrink: 0;
  }

  .menu-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    color: #333;
    flex-shrink: 0;
  }

  .menu-toggle:active {
    background: #f0f0f0;
  }

  .mobile-title {
    flex: 1;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-spacer {
    width: 36px;
    flex-shrink: 0;
  }

  .main-area {
    width: 100%;
  }
}

/* ====== 平板 769px~1024px ====== */
@media (min-width: 769px) and (max-width: 1024px) {
  /* 平板下侧边栏稍窄 */
}

/* ====== 登录弹窗（根层级，不受侧边栏限制） ====== */
.login-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: overlayIn 0.2s ease;
}

@keyframes overlayIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.login-dialog {
  position: relative;
  width: 380px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: dialogIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes dialogIn {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.login-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
  transition: all 0.2s;
}

.login-close:hover {
  background: #f0f0f0;
  color: #333;
}

.login-header {
  text-align: center;
  margin-bottom: 24px;
}

.login-icon {
  font-size: 40px;
  margin-bottom: 12px;
}

.login-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 6px;
}

.login-header p {
  font-size: 13px;
  color: #999;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field label {
  font-size: 13px;
  font-weight: 500;
  color: #555;
}

.required {
  color: #ff4d4f;
  margin-left: 2px;
}

.input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1.5px solid #e4e4e7;
  border-radius: 10px;
  transition: border-color 0.2s;
}

.input-wrap:focus-within {
  border-color: #1a9c8e;
}

.input-wrap input {
  flex: 1;
  padding: 10px 0;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: #1a1a1a;
}

.input-wrap input::placeholder {
  color: #bbb;
}

.toggle-pwd {
  padding: 4px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-error {
  font-size: 13px;
  color: #e74c3c;
  padding: 8px 12px;
  background: #fef0ef;
  border-radius: 8px;
}

.login-submit {
  width: 100%;
  padding: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background: #1a9c8e;
  border-radius: 10px;
  transition: all 0.2s;
  margin-top: 4px;
}

.login-submit:hover:not(:disabled) {
  background: #158f82;
}

.login-submit:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.login-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}

.link-text {
  font-size: 13px;
  color: #1a9c8e;
  cursor: pointer;
  transition: opacity 0.2s;
}

.link-text:hover {
  opacity: 0.7;
}

.code-wrap {
  padding-right: 4px;
}

.code-wrap input {
  min-width: 0;
}

.send-code-btn {
  padding: 6px 10px;
  font-size: 12px;
  color: #1a9c8e;
  white-space: nowrap;
  flex-shrink: 0;
  border-radius: 6px;
  transition: background 0.2s;
}

.send-code-btn:hover:not(:disabled) {
  background: #f0fbf9;
}

.send-code-btn:disabled {
  color: #bbb;
  cursor: not-allowed;
}

/* ====== 登录弹窗移动端自适应 ====== */
@media (max-width: 768px) {
  .login-overlay {
    padding: 16px;
    align-items: center;
    justify-content: center;
  }

  .login-dialog {
    width: calc(100vw - 32px);
    max-width: 360px;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    border-radius: 16px;
    padding: 28px 22px 20px;
    animation: dialogInMobile 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes dialogInMobile {
    from {
      opacity: 0;
      transform: scale(0.94) translateY(16px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .login-close {
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
  }

  .login-header {
    margin-bottom: 20px;
  }

  .login-icon {
    font-size: 34px;
    margin-bottom: 8px;
  }

  .login-form {
    gap: 14px;
  }

  .input-wrap {
    padding: 0 12px;
  }

  .input-wrap input {
    padding: 11px 0;
    font-size: 16px;
  }

  .login-submit {
    padding: 13px;
    font-size: 16px;
  }

  .login-footer {
    flex-direction: row;
    gap: 10px;
    align-items: center;
  }

  .link-text {
    font-size: 14px;
  }
}

/* ====== 超小屏 ====== */
@media (max-width: 380px) {
  .login-dialog {
    padding: 24px 14px 16px;
  }

  .login-form {
    gap: 12px;
  }

  .input-wrap {
    padding: 0 8px;
  }

  .input-wrap input {
    font-size: 15px;
  }
}

/* ====== 横屏移动端 ====== */
@media (max-width: 900px) and (orientation: landscape) {
  .login-overlay {
    align-items: center;
    padding: 12px;
  }

  .login-dialog {
    width: 100%;
    max-width: 480px;
    max-height: calc(100vh - 24px);
    border-radius: 16px;
    animation: dialogIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .login-header {
    margin-bottom: 14px;
  }

  .login-icon {
    font-size: 28px;
    margin-bottom: 4px;
  }

  .login-form {
    gap: 12px;
  }

  .input-wrap input {
    padding: 8px 0;
    font-size: 15px;
  }

  .login-submit {
    padding: 10px;
    margin-top: 2px;
  }
}
</style>
